import {
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useRef, useState } from "react";
import axios from "axios";
import { StyledButton } from "../../common/Buttons";
import { EventType } from "../../../interfaces";
import { axiosInstance } from "../../../utils/networking";

// A blank event shaped like the Eventbrite documents the scraper writes, so
// manually created events render through the same cards and email templates.
export const emptyEvent: any = {
  url: "",
  name: { text: "" },
  description: { text: "" },
  logo: { url: "" },
  flyer: null,
  start: { local: "", timezone: "America/Detroit" },
  end: { local: "", timezone: "America/Detroit" },
  locationInfo: {
    name: "",
    localized_address_display: "",
    address: {
      address_1: "",
      address_2: "",
      city: "",
      region: "MI",
      postal_code: "",
      country: "US",
    },
  },
};

const EventEdit = (props: any) => {
  const [event, setEvent] = useState<any>(
    props.event ? JSON.parse(JSON.stringify(props.event)) : { ...emptyEvent },
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [flyerUploading, setFlyerUploading] = useState(false);
  const flyerInputRef = useRef<HTMLInputElement>(null);
  const isEdit = Boolean(event._id);

  const handleFlyerUpload = async (file: File | undefined) => {
    if (!file) return;
    setError("");
    setFlyerUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await axios.post(`/upload/attachment`, formData, {
        baseURL: process.env.REACT_APP_API_DOMAIN,
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEvent({
        ...event,
        flyer: {
          name: result.data.attachmentName,
          storageName: result.data.storageName,
          path: `/public/email/upload/` + result.data.storageName,
          download: `${process.env.REACT_APP_API_DOMAIN}/upload/attachment/${result.data.storageName}/${result.data.attachmentName}`,
        },
      });
    } catch (err) {
      setError("Flyer upload failed. Please try again.");
    } finally {
      setFlyerUploading(false);
      if (flyerInputRef.current) flyerInputRef.current.value = "";
    }
  };

  const setField = (path: string[], value: string) => {
    const next = { ...event };
    let target = next;
    for (const key of path.slice(0, -1)) {
      target[key] = { ...target[key] };
      target = target[key];
    }
    target[path[path.length - 1]] = value;
    setEvent(next);
  };

  const handleSave = () => {
    setError("");
    setSaving(true);
    axiosInstance
      .post("/events", event)
      .then((res) => {
        props.onSaved(res.data.event);
        props.setOpen(false);
      })
      .catch((err) => {
        setError(err.response?.data?.msg || "Could not save the event.");
      })
      .finally(() => setSaving(false));
  };

  return (
    <Box sx={{ padding: "20px" }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "5px" }}>
        {isEdit ? "Edit Event" : "Add Event"}
      </Typography>

      {isEdit && event.source !== "manual" && (
        <Alert severity="info" sx={{ marginBottom: "10px" }}>
          This event came from Eventbrite. Fields you change here will be kept
          and no longer overwritten by the nightly Eventbrite sync.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ marginBottom: "10px" }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2} sx={{ marginTop: "10px" }}>
        <TextField
          label="Event Name"
          required
          fullWidth
          value={event.name?.text || ""}
          onChange={(e) => setField(["name", "text"], e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={4}
          value={event.description?.text || ""}
          onChange={(e) => setField(["description", "text"], e.target.value)}
          helperText="Shown on the event card and in the invitation email."
        />
        <TextField
          label="Registration / Event URL"
          fullWidth
          value={event.url || ""}
          onChange={(e) => setField(["url"], e.target.value)}
        />
        <TextField
          label="Logo Image URL"
          fullWidth
          value={event.logo?.url || ""}
          onChange={(e) => setField(["logo", "url"], e.target.value)}
          helperText="A single image URL — not a srcset list."
        />

        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Event Flyer
          </Typography>
          <input
            ref={flyerInputRef}
            type="file"
            hidden
            onChange={(e) => handleFlyerUpload(e.target.files?.[0])}
          />
          {event.flyer ? (
            <Chip
              icon={<AttachFileIcon />}
              label={event.flyer.name}
              onDelete={() => setEvent({ ...event, flyer: null })}
            />
          ) : (
            <StyledButton
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={flyerUploading}
              onClick={() => flyerInputRef.current?.click()}
            >
              {flyerUploading ? "Uploading…" : "Upload Flyer"}
            </StyledButton>
          )}
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Attached automatically when this event's invitation is broadcast.
          </Typography>
        </Box>

        <Divider textAlign="left">
          <Typography variant="body2" color="text.secondary">
            Date &amp; Time (local)
          </Typography>
        </Divider>
        {/* Phones: one per line, so the time stays visible. */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Starts"
            type="datetime-local"
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={(event.start?.local || "").slice(0, 16)}
            onChange={(e) =>
              setField(["start", "local"], `${e.target.value}:00`)
            }
          />
          <TextField
            label="Ends"
            type="datetime-local"
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={(event.end?.local || "").slice(0, 16)}
            onChange={(e) => setField(["end", "local"], `${e.target.value}:00`)}
          />
        </Stack>

        <Divider textAlign="left">
          <Typography variant="body2" color="text.secondary">
            Location
          </Typography>
        </Divider>
        <TextField
          label="Venue Name"
          fullWidth
          value={event.locationInfo?.name || ""}
          onChange={(e) => setField(["locationInfo", "name"], e.target.value)}
        />
        <TextField
          label="Display Address"
          fullWidth
          value={event.locationInfo?.localized_address_display || ""}
          onChange={(e) =>
            setField(
              ["locationInfo", "localized_address_display"],
              e.target.value,
            )
          }
          helperText="The single-line address shown on the card and in emails."
        />
        <Stack direction={"row"} spacing={2}>
          <TextField
            label="Address 1"
            fullWidth
            value={event.locationInfo?.address?.address_1 || ""}
            onChange={(e) =>
              setField(["locationInfo", "address", "address_1"], e.target.value)
            }
          />
          <TextField
            label="Address 2"
            fullWidth
            value={event.locationInfo?.address?.address_2 || ""}
            onChange={(e) =>
              setField(["locationInfo", "address", "address_2"], e.target.value)
            }
          />
        </Stack>
        {/* Phones: City on its own line, State and Zip below it. */}
        <Stack
          direction={"row"}
          spacing={2}
          useFlexGap
          sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}
        >
          <TextField
            label="City"
            fullWidth
            value={event.locationInfo?.address?.city || ""}
            onChange={(e) =>
              setField(["locationInfo", "address", "city"], e.target.value)
            }
          />
          <TextField
            label="State"
            sx={{ width: "120px" }}
            value={event.locationInfo?.address?.region || ""}
            onChange={(e) =>
              setField(["locationInfo", "address", "region"], e.target.value)
            }
          />
          <TextField
            label="Zip"
            sx={{ width: "160px" }}
            value={event.locationInfo?.address?.postal_code || ""}
            onChange={(e) =>
              setField(
                ["locationInfo", "address", "postal_code"],
                e.target.value,
              )
            }
          />
        </Stack>

        <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
          <StyledButton onClick={() => props.setOpen(false)}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save Event"}
          </StyledButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default EventEdit;
